/**
 * Simple Authentication Hook for Tammy's Self Service
 *
 * Phase B: server-verified login (session cookie) + local UI session.
 * Falls back to client-only check only when server login is not configured (local dev).
 */

import { useState, useEffect, useCallback } from 'react';
import { adminLogin, adminLogout, checkAdminSession } from '../lib/adminApi';

const SESSION_KEY = 'nanny_b_admin_session';
const SESSION_EXPIRY_DAYS = 30;

const LOCKOUT_KEY = 'nanny_b_login_lockout';
const MAX_ATTEMPTS = 5;
const LOCKOUT_DURATION_MS = 15 * 60 * 1000;

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: { email: string } | null;
}

interface UseAuthReturn extends AuthState {
  login: (email: string, password: string, rememberMe?: boolean) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}

function getStoredSession(): { email: string; expiry: number } | null {
  try {
    const stored = localStorage.getItem(SESSION_KEY);
    if (!stored) return null;
    const session = JSON.parse(stored);
    if (session.expiry && Date.now() < session.expiry) {
      return session;
    }
    localStorage.removeItem(SESSION_KEY);
    return null;
  } catch {
    return null;
  }
}

function storeSession(email: string, rememberMe = true): void {
  const expiryDays = rememberMe ? SESSION_EXPIRY_DAYS : 1;
  const expiry = Date.now() + expiryDays * 24 * 60 * 60 * 1000;
  localStorage.setItem(SESSION_KEY, JSON.stringify({ email, expiry }));
}

function clearSession(): void {
  localStorage.removeItem(SESSION_KEY);
}

interface LockoutState {
  attempts: number;
  lockedUntil: number | null;
}

function getLockoutState(): LockoutState {
  try {
    const stored = localStorage.getItem(LOCKOUT_KEY);
    if (!stored) return { attempts: 0, lockedUntil: null };
    const state = JSON.parse(stored);
    if (state.lockedUntil && Date.now() > state.lockedUntil) {
      localStorage.removeItem(LOCKOUT_KEY);
      return { attempts: 0, lockedUntil: null };
    }
    return state;
  } catch {
    return { attempts: 0, lockedUntil: null };
  }
}

function recordFailedAttempt(): { isLocked: boolean; remainingAttempts: number; lockoutMinutes: number } {
  const state = getLockoutState();
  const newAttempts = state.attempts + 1;
  if (newAttempts >= MAX_ATTEMPTS) {
    const lockedUntil = Date.now() + LOCKOUT_DURATION_MS;
    localStorage.setItem(LOCKOUT_KEY, JSON.stringify({ attempts: newAttempts, lockedUntil }));
    return { isLocked: true, remainingAttempts: 0, lockoutMinutes: 15 };
  }
  localStorage.setItem(LOCKOUT_KEY, JSON.stringify({ attempts: newAttempts, lockedUntil: null }));
  return { isLocked: false, remainingAttempts: MAX_ATTEMPTS - newAttempts, lockoutMinutes: 0 };
}

function clearFailedAttempts(): void {
  localStorage.removeItem(LOCKOUT_KEY);
}

function checkLockout(): { isLocked: boolean; minutesRemaining: number } {
  const state = getLockoutState();
  if (state.lockedUntil && Date.now() < state.lockedUntil) {
    const minutesRemaining = Math.ceil((state.lockedUntil - Date.now()) / 60000);
    return { isLocked: true, minutesRemaining };
  }
  return { isLocked: false, minutesRemaining: 0 };
}

/** Dev-only fallback when ADMIN_PASSWORD is not set on the server. */
function clientOnlyLogin(email: string, password: string): boolean {
  const adminEmail = import.meta.env.VITE_ADMIN_EMAIL || 'nannybbakedgoods@outlook.com';
  const adminPassword = import.meta.env.VITE_ADMIN_PASSWORD || 'Tammeee123!';
  return (
    email.toLowerCase() === adminEmail.toLowerCase() &&
    password === adminPassword
  );
}

export function useAuth(): UseAuthReturn {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stored = getStoredSession();
      if (stored) {
        const server = await checkAdminSession();
        if (!cancelled) {
          if (server.ok) {
            setState({
              isAuthenticated: true,
              isLoading: false,
              user: { email: server.email || stored.email },
            });
            return;
          }
          // Cookie expired but local session exists — require re-login for API access
          clearSession();
        }
      }
      if (!cancelled) {
        setState({ isAuthenticated: false, isLoading: false, user: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string, rememberMe = true) => {
    const lockout = checkLockout();
    if (lockout.isLocked) {
      return {
        success: false,
        error: `Too many failed attempts. Try again in ${lockout.minutesRemaining} minute${lockout.minutesRemaining !== 1 ? 's' : ''}.`,
      };
    }

    if (!email || !password) {
      return { success: false, error: 'Please enter email and password' };
    }

    const serverResult = await adminLogin(email, password, rememberMe);
    if (serverResult.ok) {
      clearFailedAttempts();
      storeSession(email, rememberMe);
      setState({ isAuthenticated: true, isLoading: false, user: { email } });
      return { success: true };
    }

    // Server not configured (503) — local dev fallback only
    if (serverResult.error?.includes('not configured') && clientOnlyLogin(email, password)) {
      clearFailedAttempts();
      storeSession(email, rememberMe);
      setState({ isAuthenticated: true, isLoading: false, user: { email } });
      return { success: true };
    }

    const result = recordFailedAttempt();
    if (result.isLocked) {
      return { success: false, error: `Too many failed attempts. Account locked for ${result.lockoutMinutes} minutes.` };
    }
    return {
      success: false,
      error: serverResult.error || `Invalid email or password. ${result.remainingAttempts} attempt${result.remainingAttempts !== 1 ? 's' : ''} remaining.`,
    };
  }, []);

  const logout = useCallback(() => {
    void adminLogout();
    clearSession();
    setState({ isAuthenticated: false, isLoading: false, user: null });
  }, []);

  return { ...state, login, logout };
}
